import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calculator, DollarSign, ArrowUp, ArrowDown, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Cash() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [initialCash, setInitialCash] = useState('');
  const [finalCash, setFinalCash] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchSession();
  }, []);

  const fetchSession = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cash-sessions/current`, { withCredentials: true });
      setSession(data);
    } catch (error) {
      // Don't show error toast for auth errors - user will be redirected to login
      if (error.response?.status !== 401) {
        console.error('Error fetching session:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCash = async () => {
    if (!initialCash || parseFloat(initialCash) < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/cash-sessions/open`, { initial_cash: parseFloat(initialCash) }, { withCredentials: true });
      toast.success('Caja abierta correctamente');
      setOpenModal(false);
      setInitialCash('');
      fetchSession();
    } catch (error) {
      // Don't show toast for auth errors (handled by interceptor)
      if (!error.isAuthError) {
        toast.error(error.response?.data?.detail || 'Error al abrir caja');
      }
    }
  };

  const handleCloseCash = async () => {
    if (!finalCash || parseFloat(finalCash) < 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/cash-sessions/close`, {
        final_cash: parseFloat(finalCash),
        notes
      }, { withCredentials: true });
      toast.success('Caja cerrada correctamente');
      setCloseModal(false);
      setFinalCash('');
      setNotes('');
      fetchSession();
    } catch (error) {
      // Don't show toast for auth errors (handled by interceptor)
      if (!error.isAuthError) {
        toast.error(error.response?.data?.detail || 'Error al cerrar caja');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount || 0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('es-CL', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Caja</h1>
        <p className="text-stone-500 mt-1">Gestión de apertura y cierre de caja</p>
      </div>

      {/* No session */}
      {!session ? (
        <Card className="bg-white border-stone-200 rounded-2xl" data-testid="cash-closed-card">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 rounded-2xl bg-stone-100 flex items-center justify-center mb-6">
              <Calculator className="w-10 h-10 text-stone-400" />
            </div>
            <h2 className="text-xl font-bold text-stone-900 mb-2">Caja Cerrada</h2>
            <p className="text-stone-500 mb-6 text-center max-w-md">
              Para realizar ventas necesitas abrir la caja ingresando el monto inicial
            </p>
            <Button
              onClick={() => setOpenModal(true)}
              data-testid="open-cash-btn"
              className="h-12 px-8 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold active:scale-[0.98]"
            >
              <ArrowUp className="w-5 h-5 mr-2" />
              Abrir Caja
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Session active */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white border-stone-200 rounded-2xl" data-testid="cash-initial-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-emerald-500" />
                  Monto Inicial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-stone-900">{formatCurrency(session.initial_cash)}</p>
                <p className="text-sm text-stone-500 mt-1">Abierta: {formatDateTime(session.opened_at)}</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-stone-200 rounded-2xl" data-testid="cash-sales-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-blue-500" />
                  Ventas del Turno
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-stone-900">{formatCurrency(session.total_sales)}</p>
                <p className="text-sm text-stone-500 mt-1">{session.sales_count} transacciones</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-stone-200 rounded-2xl" data-testid="cash-expected-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-amber-500" />
                  Total Esperado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(session.expected_cash)}</p>
                <p className="text-sm text-stone-500 mt-1">Inicial + Ventas</p>
              </CardContent>
            </Card>
          </div>

          {/* Close button */}
          <Card className="bg-white border-stone-200 rounded-2xl">
            <CardContent className="flex items-center justify-between py-6">
              <div>
                <h3 className="text-lg font-bold text-stone-900">Caja Abierta</h3>
                <p className="text-stone-500">Cajero: {session.cashier_name}</p>
              </div>
              <Button
                onClick={() => setCloseModal(true)}
                data-testid="close-cash-btn"
                className="h-12 px-8 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold active:scale-[0.98]"
              >
                <ArrowDown className="w-5 h-5 mr-2" />
                Cerrar Caja
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Open Modal */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-md" data-testid="open-cash-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Abrir Caja</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Monto inicial en caja</Label>
              <Input
                type="number"
                value={initialCash}
                onChange={(e) => setInitialCash(e.target.value)}
                placeholder="0"
                data-testid="initial-cash-input"
                className="h-12 rounded-xl text-lg"
              />
            </div>
            <Button
              onClick={handleOpenCash}
              data-testid="confirm-open-cash-btn"
              className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              Confirmar Apertura
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Modal */}
      <Dialog open={closeModal} onOpenChange={setCloseModal}>
        <DialogContent className="sm:max-w-md" data-testid="close-cash-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Cerrar Caja</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-sm text-stone-500">Total esperado en caja</p>
              <p className="text-2xl font-bold text-stone-900">{formatCurrency(session?.expected_cash)}</p>
            </div>
            <div>
              <Label>Monto contado en caja</Label>
              <Input
                type="number"
                value={finalCash}
                onChange={(e) => setFinalCash(e.target.value)}
                placeholder="0"
                data-testid="final-cash-input"
                className="h-12 rounded-xl text-lg"
              />
              {finalCash && session && (
                <p className={`text-sm mt-2 ${parseFloat(finalCash) - session.expected_cash >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Diferencia: {formatCurrency(parseFloat(finalCash) - session.expected_cash)}
                </p>
              )}
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones del arqueo..."
                data-testid="close-cash-notes"
                className="rounded-xl"
              />
            </div>
            <Button
              onClick={handleCloseCash}
              data-testid="confirm-close-cash-btn"
              className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Confirmar Cierre
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
